// src/gestion-logistics/hooks/useLogisticsDashboard.ts
import { useCallback, useState } from "react";
import axios from "axios";
import { message } from "antd";
import type { Dayjs } from "dayjs";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import {
  LogisticsDashboardKpis,
  LogisticsRevenueCostPoint,
  LogisticsProfitabilityResponse,
  LogisticsOutstandingResponse,
  LogisticsTransportModeStat,
} from "../types/dashboard.types";

interface DateRangeParams {
  startDate?: string | Dayjs;
  endDate?: string | Dayjs;
}

const formatDateParam = (value?: string | Dayjs) => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  return value.format("YYYY-MM-DD");
};

export const useLogisticsDashboard = () => {
  const { selectedCompany } = useSelection();
  const [loading, setLoading] = useState(false);
  const [kpis, setKpis] = useState<LogisticsDashboardKpis | null>(null);
  const [revenueCost, setRevenueCost] = useState<LogisticsRevenueCostPoint[]>([]);
  const [profitability, setProfitability] = useState<LogisticsProfitabilityResponse>({
    top: [],
    loss: [],
  });
  const [outstanding, setOutstanding] = useState<LogisticsOutstandingResponse>({
    invoices: [],
    supplierBills: [],
  });
  const [transportModes, setTransportModes] = useState<LogisticsTransportModeStat[]>([]);

  const resetData = useCallback(() => {
    setKpis(null);
    setRevenueCost([]);
    setProfitability({ top: [], loss: [] });
    setOutstanding({ invoices: [], supplierBills: [] });
    setTransportModes([]);
  }, []);

  const fetchDashboard = useCallback(
    async (range: DateRangeParams = {}) => {
      if (!selectedCompany) {
        resetData();
        return;
      }

      setLoading(true);

      const params: Record<string, string | number | undefined> = {
        company_id: selectedCompany,
        start_date: formatDateParam(range.startDate),
        end_date: formatDateParam(range.endDate),
      };

      try {
        // Endpoint agrégé (réduit le burst initial / pool MySQL)
        try {
          const summaryResponse = await axios.get(
            `${API_CONFIG.API_URL}/logistics/dashboard/summary`,
            { params }
          );

          if (summaryResponse.data?.success) {
            const data = summaryResponse.data.data || {};
            setKpis(data.kpis || null);
            setRevenueCost(data.revenueCost || []);
            setProfitability(data.profitability || { top: [], loss: [] });
            setOutstanding(data.outstanding || { invoices: [], supplierBills: [] });
            setTransportModes(data.transportModes || []);
            return;
          }
        } catch (summaryError) {
          // Fallback: anciens endpoints si summary indisponible
        }

        const results = await Promise.allSettled([
          axios.get(`${API_CONFIG.API_URL}/logistics/dashboard/kpis`, { params }),
          axios.get(`${API_CONFIG.API_URL}/logistics/dashboard/revenue-costs`, { params }),
          axios.get(`${API_CONFIG.API_URL}/logistics/dashboard/profitability`, { params }),
          axios.get(`${API_CONFIG.API_URL}/logistics/dashboard/outstanding`, { params }),
          axios.get(`${API_CONFIG.API_URL}/logistics/dashboard/transport-modes`, { params }),
        ]);

        const errors: string[] = [];

        if (results[0].status === "fulfilled") {
          setKpis(results[0].value.data.data);
        } else {
          errors.push("KPIs");
        }

        if (results[1].status === "fulfilled") {
          setRevenueCost(results[1].value.data.data || []);
        } else {
          errors.push("graphique revenus/couts");
        }

        if (results[2].status === "fulfilled") {
          setProfitability(results[2].value.data.data || { top: [], loss: [] });
        } else {
          errors.push("profitabilite");
        }

        if (results[3].status === "fulfilled") {
          setOutstanding(results[3].value.data.data || { invoices: [], supplierBills: [] });
        } else {
          errors.push("encours");
        }

        if (results[4].status === "fulfilled") {
          setTransportModes(results[4].value.data.data || []);
        } else {
          errors.push("modes de transport");
        }

        if (errors.length > 0) {
          message.warning(
            `Certaines donnees du dashboard n'ont pas pu etre chargees: ${errors.join(", ")}`
          );
        }
      } catch (error: any) {
        console.error("[LOGISTICS] Error fetching dashboard:", error);
        message.error(
          error.response?.data?.message ||
            "Erreur lors du chargement du dashboard logistique"
        );
      } finally {
        setLoading(false);
      }
    },
    [resetData, selectedCompany]
  );

  return {
    loading,
    kpis,
    revenueCost,
    profitability,
    outstanding,
    transportModes,
    fetchDashboard,
    selectedCompany,
  };
};
